import { cn } from "@/lib/utils";
import { Blend, Edit, Plus, Trash2, Workflow, X } from "lucide-react";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import { FilterInstance } from "../../hooks/use-object-list-filters";
import { DRQBuilder } from "@/utils/drq";
import { Filter } from "../layout/types";
import { AddFilterComponent } from "./add-filter-component";
import { FieldLabelWithDescription } from "./field-label-with-description";
import { linkedFilterFromPath } from "./filter-tree/linked-filter";
import { humanizePath } from "./filter-tree/tree-utils";
import { useResolvedPath } from "./filter-tree/use-filter-schema";


/** Label of a filter on a field of a linked object: the labels of the fields it walks through. */
function PathLabel({ rootModel, path, className }: { rootModel?: string; path: string; className?: string }) {
    const resolved = useResolvedPath(rootModel ?? "", rootModel ? path : "");
    return <span className={className}>{resolved ? resolved.labels.join(" › ") : humanizePath(path)}</span>;
}

function validateLocalFiltersForDrq(instances: FilterInstance[], allFilters: Filter[]): string | null {
    for (const inst of instances) {
        const def = allFilters.find(f => f.field_name === inst.field_name);
        if (!def) continue;
        const ft = def.type;
        if (ft === "date-range" || ft === "datetime-range") {
            const r = DRQBuilder.validateTemporalFilterInput(inst.value, inst.operator, ft);
            if (!r.valid) {
                return r.error || `Invalid date filter for ${def.name || def.field_name}`;
            }
        }
    }
    return null;
}

interface ObjectFilterProps extends React.HTMLAttributes<HTMLDivElement> {
    filter: Filter;
    filterInstance: FilterInstance;
    onRemove?: () => void;
    onEdit?: (e: React.MouseEvent<SVGSVGElement>) => void;
    /** "app_label.model_name" of the list, to label filters on linked objects. */
    rootModel?: string;
    isUnknownField?: boolean;
}

function ObjectFilter({ filter, filterInstance, onRemove, onEdit, rootModel, isUnknownField }: ObjectFilterProps) {
    // Fonction pour obtenir le label de l'opérateur en français
    const getOperatorLabel = (op?: string) => {
        switch (op) {
            case "contains":
                return "contains";
            case "equals":
                return "equals";
            case "not_contains":
                return "does not contain";
            case "not_equal_to":
                return "not equal to";
            case "starts_with":
                return "starts with";
            case "ends_with":
                return "ends with";
            case "in":
                return "contains";
            case "not_in":
                return "does not contain";
            case "is_empty":
                return "is empty";
            case "is_not_empty":
                return "is not empty";
            case "gt":
                return "greater than";
            case "lt":
                return "less than";
            case "gte":
                return "greater than or equal to";
            case "lte":
                return "less than or equal to";
            default:
                return "equals";
        }
    };

    // Fonction pour formater la valeur d'affichage selon le type
    const getDisplayValue = () => {
        const type = filter.type || "text";
        const value = filterInstance.value;

        if (filterInstance.operator === "is_empty") {
            return filterInstance.value === "True" ? "is empty" : "is not empty";
        } else if (filterInstance.operator === "is_not_empty") {
            return filterInstance.value === "True" ? "is not empty" : "is empty";
        }

        switch (type) {
            case "select":
                const selectedOption = filter.options?.find(option => option.value === value);
                return selectedOption?.label || value;

            case "combobox":
                return value?.split(",").join(", ") || value;

            case "date-range":
            case "datetime-range":
                // Si la valeur contient des virgules, c'est probablement une plage de dates ISO
                if (value?.includes(",")) {
                    const [from, to] = value.split(",");
                    try {
                        const fromDate = DateTime.fromISO(from);
                        const toDate = DateTime.fromISO(to);
                        if (fromDate.isValid && toDate.isValid) {
                            return `${fromDate.toFormat("dd/MM/yyyy")} - ${toDate.toFormat("dd/MM/yyyy")}`;
                        }
                    } catch (error) {
                        // Si le parsing échoue, garder la valeur originale
                    }
                } else if (value) {
                    // Pour une date simple, essayer de la formater seulement si c'est une date ISO
                    try {
                        // Vérifier si c'est une date ISO valide
                        if (value.match(/^\d{4}-\d{2}-\d{2}T/)) {
                            const singleDate = DateTime.fromISO(value);
                            if (singleDate.isValid) {
                                return singleDate.toFormat("dd/MM/yyyy");
                            }
                        }
                        // Si ce n'est pas une date ISO, c'est probablement une date relative, retourner tel quel
                        return value;
                    } catch (error) {
                        // Si le parsing échoue, garder la valeur originale
                    }
                }
                return value;

            case "checkbox":
                return value === "True" || value === "1" ? "True" : "False";

            default:
                return value;
        }
    };

    const operatorLabel = getOperatorLabel(filterInstance.operator);
    const displayValue = getDisplayValue();

    // Structure commune pour tous les types
    return (
        <div className="flex w-full max-w-sm items-center justify-between">
            <div className="flex flex-col gap-0">
                {isUnknownField ? (
                    <PathLabel rootModel={rootModel} path={filter.field_name} className="text-[12px] text-gray-600" />
                ) : (
                    <FieldLabelWithDescription label={filter.name ?? filter.field_name} description={filter.description} className="text-[12px] text-gray-600" />
                )}
                <span>
                    {operatorLabel} {operatorLabel !== "is empty" && operatorLabel !== "is not empty" && <span className="font-semibold">{displayValue}</span>}
                </span>
            </div>
            <div className="flex space-x-2">
                <Trash2 className="h-4 w-4 cursor-pointer" onClick={onRemove} />
                <Edit className="h-4 w-4 cursor-pointer" onClick={onEdit} />
            </div>
        </div>
    );
}

// Fonction de validation pour l'input de logique de filtres
function validateFilterLogicInput(input: string, filterCount: number): { isValid: boolean; error?: string } {
    // Nettoyer l'input en supprimant les espaces superflus
    const cleanedInput = input.trim();

    if (!cleanedInput) {
        return { isValid: true };
    }

    // Vérifier les parenthèses équilibrées
    let parenthesesCount = 0;
    for (const char of cleanedInput) {
        if (char === "(") {
            parenthesesCount++;
        } else if (char === ")") {
            parenthesesCount--;
            if (parenthesesCount < 0) {
                return { isValid: false, error: "Parentheses not closed" };
            }
        }
    }

    if (parenthesesCount !== 0) {
        return { isValid: false, error: "Parentheses not balanced" };
    }

    // Tokeniser l'input
    const tokens = cleanedInput
        .replace(/\(/g, " ( ")
        .replace(/\)/g, " ) ")
        .split(/\s+/)
        .filter(token => token.length > 0);

    // Vérifier chaque token
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];

        // Vérifier si c'est un nombre (index de filtre)
        if (/^\d+$/.test(token)) {
            const index = parseInt(token);
            if (index < 1 || index > filterCount) {
                return { isValid: false, error: `Invalid filter index: ${index} (must be between 1 and ${filterCount})` };
            }
        }
        // Vérifier si c'est un opérateur logique
        else if (token.toUpperCase() === "AND" || token.toUpperCase() === "OR") {
            // Vérifier qu'il y a des tokens avant et après l'opérateur
            if (i === 0 || i === tokens.length - 1) {
                return { isValid: false, error: `Operator ${token} is not placed correctly` };
            }

            // Vérifier que les tokens avant et après ne sont pas des opérateurs
            const prevToken = tokens[i - 1];
            const nextToken = tokens[i + 1];

            if (prevToken === "AND" || prevToken === "OR" || nextToken === "AND" || nextToken === "OR") {
                return { isValid: false, error: `Operator ${token} is adjacent to another operator` };
            }
        }
        // Vérifier les parenthèses
        else if (token === "(" || token === ")") {
            // Les parenthèses sont déjà vérifiées plus haut
            continue;
        }
        // Token invalide
        else {
            return { isValid: false, error: `Invalid token: ${token}` };
        }
    }

    // Vérifier qu'il y a au moins un index de filtre
    const hasFilterIndex = tokens.some(token => /^\d+$/.test(token));
    if (!hasFilterIndex) {
        return { isValid: false, error: "No filter index found" };
    }

    // Vérifier que tous les index de filtres sont utilisés
    const usedIndexes = new Set<number>();
    tokens.forEach(token => {
        if (/^\d+$/.test(token)) {
            usedIndexes.add(parseInt(token));
        }
    });

    const expectedIndexes = new Set<number>();
    for (let i = 1; i <= filterCount; i++) {
        expectedIndexes.add(i);
    }

    const missingIndexes = Array.from(expectedIndexes).filter(index => !usedIndexes.has(index));
    if (missingIndexes.length > 0) {
        return { isValid: false, error: `Missing filter indexes: ${missingIndexes.join(", ")}` };
    }

    return { isValid: true };
}

export function ObjectListFilters2({
    filters: declaredFilters,
    filterState: originalFilterState,
    onAddFilter,
    onUpdateFilter,
    onRemoveFilter,
    onClearAllFilters: _onClearAllFilters,
    currentLogic,
    onSetLogic,
    onClose,
    onOpenAdvanced,
    rootModel,
    title = "Active filters",
    searchButtonText = "Search"
}: {
    filters: Filter[];
    filterState: FilterInstance[];
    onAddFilter: (filter: Filter, value: string, operator: string) => void;
    onUpdateFilter: (filterId: string, filter: Filter, value: string, operator: string) => void;
    onRemoveFilter: (filterId: string) => void;
    onClearAllFilters: () => void;
    currentLogic?: string;
    onSetLogic?: (logic: string) => void;
    onClose?: () => void;
    /** Opens the advanced filters dialog (linked objects, related records, nested groups) on the
     * panel's draft: filters added or edited but not searched yet are part of it. */
    onOpenAdvanced?: (draft: { filters: FilterInstance[]; logic: string }) => void;
    /** "app_label.model_name" of the list, to label filters on linked objects. */
    rootModel?: string;
    title?: string;
    searchButtonText?: string;
}) {
    // Copie locale des filtres pour permettre des modifications sans affecter immédiatement l'état global
    const [localFilterState, setLocalFilterState] = useState<FilterInstance[]>(() => [...originalFilterState]);

    // Fields of linked objects (`registration__email`), defined from the filter schema: picked in
    // « Add a filter », or already in the view. They are edited like the object's own fields.
    const [linkedFilters, setLinkedFilters] = useState<Filter[]>([]);
    const filters = useMemo(
        () => [...declaredFilters, ...linkedFilters.filter(linked => !declaredFilters.some(f => f.field_name === linked.field_name))],
        [declaredFilters, linkedFilters]
    );
    const addLinkedFilter = (filter: Filter) => setLinkedFilters(previous => [...previous.filter(f => f.field_name !== filter.field_name), filter]);
    useEffect(() => {
        if (!rootModel) return;
        const missing = localFilterState.map(f => f.field_name).filter(name => name.includes("__") && !filters.some(f => f.field_name === name));
        let cancelled = false;
        for (const path of new Set(missing)) {
            linkedFilterFromPath(rootModel, path)
                .then(filter => filter && !cancelled && addLinkedFilter(filter))
                .catch(() => undefined);
        }
        return () => {
            cancelled = true;
        };
    }, [rootModel, localFilterState, filters]);

    const [filterLogicInput, setFilterLogicInput] = useState(currentLogic || "");
    const [filterLogicError, setFilterLogicError] = useState<string | undefined>();
    const [filterValueError, setFilterValueError] = useState<string | null>(null);

    // Synchroniser localFilterState avec originalFilterState quand ce dernier change (après sauvegarde)
    useEffect(() => {
        setLocalFilterState([...originalFilterState]);
    }, [originalFilterState]);

    useEffect(() => {
        setFilterValueError(null);
    }, [localFilterState]);

    // Synchroniser filterLogicInput avec currentLogic quand ce dernier change
    useEffect(() => {
        setFilterLogicInput(currentLogic || "");
        setFilterLogic(currentLogic ? true : false);
    }, [currentLogic]);
    const [state, setState] = useState<"default" | "edit">("default");
    const [addFilterComponentOpen, setAddFilterComponentOpen] = useState(false);
    const [editingFilterInstance, setEditingFilterInstance] = useState<FilterInstance | null>(null);
    const [filterLogic, setFilterLogic] = useState(currentLogic ? true : false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    // Fonction utilitaire pour voir si les filtres ont été modifiés
    const hasChanges = React.useMemo(() => {
        // Si filterLogic est false, la logique effective est vide
        const effectiveLogic = filterLogic ? filterLogicInput.trim() : "";
        const originalLogic = currentLogic || "";

        return JSON.stringify(localFilterState) !== JSON.stringify(originalFilterState) || effectiveLogic !== originalLogic;
    }, [localFilterState, originalFilterState, filterLogicInput, currentLogic, filterLogic]);

    const generateFilterId = (fieldName: string) => `${fieldName}_${Date.now()}_${Math.random()}`;

    // Réinitialise l'expression si le nombre de filtres change et qu'on a déjà une logique personnalisée
    useEffect(() => {
        // si on a une logique existante (currentLogic ou saisie utilisateur) on ne modifie rien
        if (filterLogicInput || !filterLogic) return;

        if (localFilterState.length > 1) {
            setFilterLogicInput(localFilterState.map((_, index) => index + 1).join(" AND "));
        }
    }, [localFilterState.length, filterLogic]);

    // Validation en temps réel de l'input de logique
    useEffect(() => {
        if (filterLogicInput) {
            const validation = validateFilterLogicInput(filterLogicInput, localFilterState.length);
            setFilterLogicError(validation.error);
        } else {
            setFilterLogicError(undefined);
        }
    }, [filterLogicInput, localFilterState.length]);

    const handleAddFilterInternal = (filterData: { field: string; operator: string; value: string }) => {
        const filter = filters.find(f => f.field_name === filterData.field);
        if (filter) {
            const newFilter: FilterInstance = {
                id: generateFilterId(filter.field_name),
                field_name: filter.field_name,
                value: filterData.value,
                operator: filterData.operator,
                type: filter.type
            };
            setLocalFilterState(prev => [...prev, newFilter]);
        }
    };

    const handleEditFilterInternal = (filterData: { field: string; operator: string; value: string }) => {
        const filter = filters.find(f => f.field_name === filterData.field);
        if (filter && editingFilterInstance) {
            setLocalFilterState(prev =>
                prev.map(f => (f.id === editingFilterInstance.id ? { ...f, field_name: filter.field_name, value: filterData.value, operator: filterData.operator, type: filter.type } : f))
            );
            setEditingFilterInstance(null);
            setState("default");
        }
    };

    const handleRemoveLocalFilter = (filterId: string) => {
        setLocalFilterState(prev => prev.filter(f => f.id !== filterId));
    };

    const handleClearLocalFilters = () => {
        setLocalFilterState([]);
    };

    const handleSave = () => {
        const drqFilterError = validateLocalFiltersForDrq(localFilterState, filters);
        if (drqFilterError) {
            setFilterValueError(drqFilterError);
            return;
        }
        setFilterValueError(null);

        // Map for quick lookup
        const originalMap = new Map(originalFilterState.map(f => [f.id, f]));
        const localMap = new Map(localFilterState.map(f => [f.id, f]));

        // Traiter ajouts et mises à jour
        localFilterState.forEach(f => {
            const orig = originalMap.get(f.id);
            if (!orig) {
                // Nouveau filtre
                const filterDef = filters.find(fl => fl.field_name === f.field_name);
                if (filterDef) {
                    onAddFilter(filterDef, f.value, f.operator);
                }
            } else if (orig.value !== f.value || orig.operator !== f.operator || orig.field_name !== f.field_name) {
                const filterDef = filters.find(fl => fl.field_name === f.field_name);
                if (filterDef) {
                    onUpdateFilter(f.id, filterDef, f.value, f.operator);
                }
            }
        });

        // Traiter suppressions
        originalFilterState.forEach(orig => {
            if (!localMap.has(orig.id)) {
                onRemoveFilter(orig.id);
            }
        });

        // Mettre à jour la logique si nécessaire
        if (onSetLogic) {
            const logicToSet = filterLogic ? filterLogicInput.trim() : "";
            onSetLogic(logicToSet);
        }

        onClose?.();
    };

    const handleEditClick = (filterInstance: FilterInstance, e: React.MouseEvent<SVGSVGElement>) => {
        setAnchorEl(e.currentTarget as unknown as HTMLElement);
        setEditingFilterInstance(filterInstance);
        setAddFilterComponentOpen(true);
        setState("edit");
    };

    return (
        <div className="flex flex-col space-y-2 p-2">
            <div className="mb-1.5 flex h-10 flex-row items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                {hasChanges ? (
                    <div className="flex flex-col items-end gap-1">
                        <button
                            onClick={handleSave}
                            disabled={!!filterLogicError}
                            className={`rounded px-3 py-1.5 text-sm font-medium ${filterLogicError ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                        >
                            {searchButtonText}
                        </button>
                        {filterValueError && <p className="max-w-xs text-right text-xs text-red-600">{filterValueError}</p>}
                    </div>
                ) : Boolean(onClose) ? (
                    <X className="cursor-pointer rounded p-0.5 hover:bg-gray-100" size={20} onClick={onClose} />
                ) : null}
            </div>
            <div className=" space-y-2">
                {localFilterState.map((filterInstance, index) => {
                    const filter = filters.find(f => f.field_name === filterInstance.field_name) ?? {
                        field_name: filterInstance.field_name,
                        name: filterInstance.field_name,
                        type: (filterInstance.type ?? "text") as Filter["type"],
                    };
                    const isUnknownField = !filters.find(f => f.field_name === filterInstance.field_name);

                    const originalEquivalent = originalFilterState.find(f => f.id === filterInstance.id);
                    const isModified =
                        !originalEquivalent ||
                        originalEquivalent.value !== filterInstance.value ||
                        originalEquivalent.operator !== filterInstance.operator ||
                        originalEquivalent.field_name !== filterInstance.field_name;

                    return (
                        <div key={filterInstance.id} className="flex w-full flex-row items-center gap-2 ">
                            {filterLogic && <div className="text-sm text-black">{index + 1}.</div>}
                            <div className={`relative w-full rounded-sm border p-4 text-sm ${isModified ? "bg-orange-100" : "bg-white"}`}>
                                <ObjectFilter
                                    filter={filter}
                                    filterInstance={filterInstance}
                                    onRemove={() => handleRemoveLocalFilter(filterInstance.id)}
                                    onEdit={isUnknownField ? undefined : e => handleEditClick(filterInstance, e)}
                                    rootModel={rootModel}
                                    isUnknownField={isUnknownField}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-row justify-between pt-6 text-sm text-blue-500">
                <span
                    className="cursor-pointer hover:underline hover:underline-offset-4"
                    onClick={e => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setAddFilterComponentOpen(true);
                        setState("default");
                    }}
                >
                    <Plus className="mr-1 inline-block" size={14} />
                    Add a filter
                </span>
                <span
                    className="cursor-pointer hover:underline hover:underline-offset-4"
                    onClick={e => {
                        e.stopPropagation();
                        handleClearLocalFilters();
                    }}
                >
                    <Trash2 className="mr-1 inline-block" size={14} />
                    Clear all filters
                </span>
            </div>
            <div className="flex flex-row justify-between  text-sm text-blue-500">
                {!filterLogic && (
                    <span
                        className={cn("cursor-pointer hover:underline hover:underline-offset-4", localFilterState.length < 2 && "cursor-not-allowed opacity-50")}
                        onClick={e => {
                            e.stopPropagation();
                            if (localFilterState.length < 2) return;

                            // Si on active la logique pour la première fois, proposer l'expression par défaut
                            if (!filterLogic) {
                                if (!filterLogicInput) {
                                    setFilterLogicInput(localFilterState.map((_, idx) => idx + 1).join(" AND "));
                                }
                            }

                            setFilterLogic(!filterLogic);
                        }}
                    >
                        <Blend className="mr-1 inline-block" size={14} />
                        Add filter logic
                    </span>
                )}
                {filterLogic && (
                    <div className="mt-4 flex w-full flex-col gap-2">
                        <div className="flex w-full flex-row justify-between gap-2  border-t py-2">
                            <h3 className="text-base font-semibold text-black">Filter logic</h3>
                            <span
                                className="cursor-pointer hover:underline hover:underline-offset-4"
                                onClick={e => {
                                    e.stopPropagation();
                                    setFilterLogic(!filterLogic);
                                    setFilterLogicInput("");
                                }}
                            >
                                <X className="mr-1 inline-block" size={14} />
                                Remove
                            </span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <input
                                type="text"
                                value={filterLogicInput}
                                placeholder="Ex: 1 AND 2 OR (3 AND 4)"
                                className={`w-full rounded-sm border p-2 text-sm text-black ${filterLogicError ? "border-red-500 bg-red-50" : "bg-white"}`}
                                onChange={e => setFilterLogicInput(e.target.value)}
                            />
                            {filterLogicError && (
                                <div className="flex items-center gap-1 text-xs text-red-500">
                                    <X className="h-3 w-3" />
                                    {filterLogicError}
                                </div>
                            )}
                            {!filterLogicError && filterLogicInput && <div className="flex items-center gap-1 text-xs text-green-600">✓ Expression valide</div>}
                        </div>
                    </div>
                )}
            </div>
            {onOpenAdvanced && (
                <div className="flex flex-row border-t pt-3 text-sm text-blue-500">
                    <span
                        className="cursor-pointer hover:underline hover:underline-offset-4"
                        onClick={e => {
                            e.stopPropagation();
                            onOpenAdvanced({ filters: localFilterState, logic: filterLogic ? filterLogicInput : "" });
                        }}
                    >
                        <Workflow className="mr-1 inline-block" size={14} />
                        Advanced filters
                    </span>
                </div>
            )}

            <AddFilterComponent
                key={`add-filter-${state}-${editingFilterInstance?.id ?? "new"}`}
                open={addFilterComponentOpen}
                setOpen={setAddFilterComponentOpen}
                availableFilters={filters}
                rootModel={rootModel}
                onLinkedFilter={addLinkedFilter}
                onAddFilter={handleAddFilterInternal}
                onEditFilter={handleEditFilterInternal}
                editMode={state === "edit"}
                initialField={editingFilterInstance?.field_name}
                initialOperator={editingFilterInstance?.operator}
                initialValue={editingFilterInstance?.value}
                anchorEl={anchorEl}
            />
        </div>
    );
}
